import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import {
  IInviteArgs,
  InviteMethod,
  InviteType,
  createSample,
  invite,
  load,
  loadSamples,
  list,
  update,
  create,
  archive,
  unarchive,
  exportJobs,
} from "common/api/job.service";
import { addFlashAlert } from "common/components/FlashAlert";
import { ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { JobActions } from "common/reducers/job.actions";
import { OrgActions } from "common/reducers/org.actions";
import { currentJob, currentJobId, resourceDocuments } from "common/selectors/job.selectors";
import { currentOrgId } from "common/selectors/org.selectors";
import { form } from "common/selectors/rsfForm.selectors";
import { RootDispatchType, RootGetState } from "common/store";
import { List } from "immutable";
import { JobParamsRecord } from "common/records/index/JobParams";
import { currentUserOrgId } from "common/selectors/user.selectors";
import { TaskActions } from "common/reducers/task.actions";
import { TokenAsyncActions } from "common/reducers/token.async.actions";
import { geoCoder } from "common/records/Address";
import { IGlobalRequestOptions } from "common/api/Fetcher";

export interface ISendInviteArgs {
  formName: string;
  invite_method: InviteMethod;
  invite_type: InviteType;
  options?: { folder_id: number };
  event_id?: number;
  summary?: string;
}

export const JobAsyncActions = {
  listJobs: (queryParams: JobParamsRecord, options?: IGlobalRequestOptions): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const modelName = "jobs";

      if (!queryParams.org_id) {
        queryParams = queryParams.set("org_id", currentUserOrgId(getState()));
      }

      if (!queryParams.org_id) {
        return addFlashAlert("danger", "No org id found.");
      }

      dispatch(IdsManagementActions.fetchModel(modelName, queryParams));

      try {
        const response = await (options ? list(queryParams, options) : list(queryParams));
        dispatch(JobActions.receiveJobOverviews(response.jobs));
        if (response.meta.state_info) dispatch(JobActions.receiveJobStates(response.meta.state_info));

        const ids = List(response.jobs.map((job) => job.id));
        dispatch(IdsManagementActions.receiveModel(modelName, ids, response?.meta?.pagination));
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.receiveModel(modelName, List()));
      }
    };
  },
  getJob: (jobId: number, setCurrentJob = true): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(JobActions.fetchJob(jobId));

      if (setCurrentJob) {
        dispatch(JobActions.setCurrentJobId(jobId));
      }

      try {
        const response = await load(jobId);

        if (setCurrentJob) {
          dispatch(OrgActions.setCurrentOrgId(response.job.org_id));
        }
        dispatch(JobActions.receiveJob(response.job));
      } catch (response) {
        dispatch(JobActions.setJobLoaded(jobId));
        const errors = handleErrors(response);
        dispatch(JobActions.setJobErrors(jobId, errors));
      }
    };
  },
  // Job Loader
  loadJob: (jobId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      await dispatch(JobAsyncActions.getJob(jobId));
      const orgId = currentOrgId(getState());
      await dispatch(TokenAsyncActions.getAuthorizedTokens(orgId));
      dispatch(TokenAsyncActions.listTokens());
    };
  },
  sendInvite: (params: ISendInviteArgs): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        const jobId = currentJobId(getState());
        const emails = form(getState(), { name: params.formName })?.get("emails");
        if (!jobId) {
          throw ["No current job id found"];
        }
        const inviteParams: IInviteArgs = { ...params, jobId, emails };
        await invite(inviteParams);
        addFlashAlert("success", "Invite successfully sent.");
      } catch (response) {
        handleErrors(response);
      }
    };
  },
  loadSampleJobs: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) {
        return addFlashAlert("danger", "No current org selected");
      }

      try {
        dispatch(IdsManagementActions.fetchModel("sampleJobs"));
        const { sample_jobs } = await loadSamples(orgId);
        dispatch(JobActions.setSampleJobs(sample_jobs));
      } catch (response) {
        handleErrors(response);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("sampleJobs"));
      }
    };
  },
  createSampleJobs: (userAssigned: number, sampleTypes: string[]) => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) {
        return addFlashAlert("danger", "No current org selected");
      }
      try {
        dispatch(IdsManagementActions.fetchModel("sampleJobs"));
        const { task } = await createSample(orgId, userAssigned, sampleTypes);
        addFlashAlert("success", "Sample Job(s) task submitted successfully.");

        if (task) {
          dispatch(TaskActions.receiveTask(task));
        }
      } catch (response) {
        handleErrors(response);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("sampleJobs"));
      }
    };
  },
  updateJob: (options?: IGlobalRequestOptions): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const jobRecord = currentJob(getState());
      if (!jobRecord) {
        addFlashAlert("danger", "No job selected");
        return;
      }

      try {
        dispatch(JobActions.fetchJob(jobRecord.id));

        const { job } = await (options ? update(jobRecord, options) : update(jobRecord));

        dispatch(JobActions.receiveJob(job));
        addFlashAlert("success", "Saved job successfully");
      } catch (errors) {
        dispatch(JobActions.setJobLoaded(jobRecord.id));
        handleErrors(errors);
      }
    };
  },
  createJob: (navigate: (path: string) => void): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const jobRecord = currentJob(getState());
      if (!jobRecord) {
        addFlashAlert("danger", "No job selected");
        return;
      }

      try {
        dispatch(JobActions.fetchJob(jobRecord.id));

        const { job } = await create(jobRecord);

        dispatch(JobActions.setJobLoaded(jobRecord.id));
        dispatch(JobActions.receiveJob(job));
        addFlashAlert("success", "Saved job successfully");
        navigate(`/jobs/${job.id}`);
      } catch (errors) {
        dispatch(JobActions.setJobLoaded(jobRecord.id));
        handleErrors(errors);
      }
    };
  },
  toggleArchiveJob: (navigate: (path: string) => void): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const jobRecord = currentJob(getState());
      if (!jobRecord) {
        addFlashAlert("danger", "No job selected");
        return;
      }

      dispatch(JobActions.fetchJob(jobRecord.id));

      if (!jobRecord.archived) {
        try {
          await archive(jobRecord);
          addFlashAlert("success", "Archived job successfully");

          navigate("/jobs");
        } catch (errors) {
          handleErrors(errors);
        } finally {
          dispatch(JobActions.setJobLoaded(jobRecord.id));
        }
      } else {
        try {
          await unarchive(jobRecord);
          dispatch(JobActions.toggleJobArchived(jobRecord.id));
          addFlashAlert("success", "Unarchived job successfully");
        } catch (errors) {
          handleErrors(errors);
        } finally {
          dispatch(JobActions.setJobLoaded(jobRecord.id));
        }
      }
    };
  },

  editJobResourceDisplay: (
    documentId: number,
    name: "displayInProposal" | "displayInAgreement",
    value: boolean,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const jobId = currentJobId(getState());
      if (jobId) {
        dispatch(JobActions.editResourceDisplay(jobId, documentId, name, value));
        dispatch(JobAsyncActions.updateJob({ useGlobalLoader: false }));
      }
    };
  },

  onSortEnd: (oldIndex: number, newIndex: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const jobId = currentJobId(getState());
      const docs = resourceDocuments(getState(), { jobId });
      const moving = docs.get(oldIndex);
      const sortedIds = docs.delete(oldIndex).insert(newIndex, moving);

      dispatch(JobActions.setResourceDocuments(jobId, sortedIds));
      dispatch(JobAsyncActions.updateJob());
    };
  },

  updateAddress: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const job = currentJob(getState());
      if (job?.address) {
        const addressRecord = await geoCoder(job.address);
        if (addressRecord) {
          dispatch(JobActions.setJobAddress(addressRecord));
        }
      }
    };
  },

  exportJobs: (queryParams: JobParamsRecord): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      if (!queryParams.org_id) {
        queryParams = queryParams.set("org_id", currentUserOrgId(getState()));
      }

      try {
        await exportJobs(queryParams);
      } catch (response) {
        handleErrors(response);
      }
    };
  },
};
